//@flow
import * as React from 'react';
import * as qs from 'query-string';
import { themr } from 'react-css-themr';
import { connect } from 'react-redux';
import {
    withRouter,
    type WithRouterProps
} from 'common/components/core/router';
import { compose } from 'recompose';

import { memoizedImmutable } from 'common/utils/Memoizer';

import { selectWithButtonFactory } from 'common/components/core/select';
import { ButtonTypes } from 'common/components/core/button';

import { IntlActions } from 'common/redux/actions/intl';
import { personnelBUsSelector } from 'common/redux/selectors/intl';
import { personnelSelector } from 'common/redux/selectors/sessionSelector';
import { getSelectedBUId } from 'common/redux/selectors/appPreferences';

import type { Personnel } from 'common/models/internal/Personnel';
import type { BusinessUnit } from 'common/models/internal/BusinessUnits';

import theme from './BusinessUnitSelector.scss';
import { listCardFactory } from 'common/components/core/card/ListCard';
import DefaultSelectComponents from 'common/components/core/select/DefaultSelectComponents';
import { clientSelector } from 'common/redux/selectors/queryParamsSelectors';
import omit from 'lodash/omit';

import coreIds from 'common/tests/core/coreIds.json';

type setSelectedBUParams = {
    clientId: string,
    personnelId: string,
    selectedBU: string
};

type Props = {
    clientId: string,
    theme: Object,
    personnel: Personnel,
    selectedBUId?: string,
    businessUnits: Array<BusinessUnit>,
    setSelectedBU: setSelectedBUParams => void,
    ...WithRouterProps
};

const BUIcon = ({ data }) => {
    return (
        <div className={theme.alias} style={{ backgroundColor: data.color }}>
            {data.shortName}
        </div>
    );
};
const OptionRender = listCardFactory({
    LeftIcon: BUIcon
});

const Option = DefaultSelectComponents.optionFactory(OptionRender);

const SelectWithButton = selectWithButtonFactory({
    Option
});

class BusinessUnitSelector extends React.PureComponent<Props> {
    @memoizedImmutable()
    getMenuOptions(businessUnits) {
        const options = [];
        businessUnits.forEach(({ businessUnitId, shortName, color, name }) => {
            options.push({
                label: name,
                value: businessUnitId,
                shortName,
                color
            });
        });
        return options;
    }

    @memoizedImmutable()
    getSelectedBUFromId(businessUnits, selectedBUId) {
        return businessUnits.find(
            ({ businessUnitId }) => businessUnitId === selectedBUId
        );
    }

    handleChange = selectedOption => {
        const {
            clientId,
            personnel,
            setSelectedBU,
            location,
            history
        } = this.props;
        const { personnelId } = personnel;
        setSelectedBU({
            clientId,
            personnelId,
            selectedBU: selectedOption.value
        });

        // reset teams when bu changed
        const query = omit(qs.parse(location.search), 'teamIds');
        history.replace({
            pathname: location.pathname,
            search: qs.stringify(query)
        });
    };

    render() {
        const { businessUnits, selectedBUId, theme } = this.props;
        const selectedBusinessUnit = this.getSelectedBUFromId(
            businessUnits,
            selectedBUId
        );
        if (!selectedBUId || !selectedBusinessUnit) {
            return null;
        }
        const { shortName, color } = selectedBusinessUnit || {};
        const options = this.getMenuOptions(businessUnits);
        return (
            <SelectWithButton
                testId={coreIds.businessUnitSelector}
                isSearchable={false}
                isClearable={false}
                theme={theme}
                semibold
                ripple={false}
                icon="caret_down"
                className={theme.buDropdownOptions}
                buttonType={ButtonTypes.Solid}
                buttonStyle={{ backgroundColor: color }}
                buttonClassName={theme.buBtn}
                options={options}
                value={selectedBUId}
                onChange={this.handleChange}
                menuAutoWidth
            >
                <div className={theme.alias} style={{ backgroundColor: color }}>
                    {shortName}
                </div>
            </SelectWithButton>
        );
    }
}

const mapStateToProps = (state, props) => {
    const clientId = clientSelector(state, props);
    const businessUnits = personnelBUsSelector(state, props);
    const selectedBUId = getSelectedBUId(state, props);
    const personnel = personnelSelector(state);
    return {
        businessUnits,
        selectedBUId,
        personnel,
        clientId
    };
};

const mapDispatchToProps = {
    setSelectedBU: IntlActions.setSelectedBU
};

const ConnectedBUSelector = compose(
    withRouter,
    connect(mapStateToProps, mapDispatchToProps),
    themr('BusinessUnitSelector', theme)
)(BusinessUnitSelector);
export default ConnectedBUSelector;
